import React, { useEffect, useMemo, useState } from 'react';
import {
  checkExpiryDateTimestamp,
  convertObjectDiscoveryToQueryStringSearch,
} from 'helpers';
import { LIMIT_ITEM_PER_PAGE_PRODUCT_LIST } from 'constants/index';
import { useSearch } from 'hooks';
import { HotDealTab, TabHeaderProps } from '@teko/teko-ecommerce';
import { getTime } from 'helpers/datetime';
import { BlockSSRProps } from 'types';
import { MultiTabSkuBlock, MultiTabSkuBlockCookedItem } from '@teko/discovery';
import { ProductListConfig } from '@teko/types/ecommerce';

type HotDealSectionProps = BlockSSRProps & {
  block: MultiTabSkuBlock;
};
type HotDealBlockCookedData = MultiTabSkuBlock['cookedData'];

export const HotDealSection: React.FunctionComponent<HotDealSectionProps> = ({
  block,
  ssrProducts = [],
}: HotDealSectionProps) => {
  const [validDeals, setValidDeals] = useState<HotDealBlockCookedData>(() =>
    (block?.cookedData || []).filter((deal) =>
      checkExpiryDateTimestamp(deal.startTime, deal.endTime),
    ),
  );
  const [activeTabId, setActiveTabId] = useState<string | undefined>(
    validDeals?.[0]?.id,
  );

  const { result } = useSearch(
    block && {
      ...(validDeals?.find((el) => el.id === activeTabId)?.paramsObject || {}),
      blockId: block?.id,
      pageSize: LIMIT_ITEM_PER_PAGE_PRODUCT_LIST,
    },
    {
      products: ssrProducts,
    },
  );

  useEffect(() => {
    setValidDeals(
      (block?.cookedData || []).filter((deal) =>
        checkExpiryDateTimestamp(deal.startTime, deal.endTime),
      ),
    );
  }, [block?.cookedData]);

  useEffect(() => {
    setActiveTabId(validDeals?.[0]?.id);
  }, [JSON.stringify(validDeals)]);

  useEffect(() => {
    if (!validDeals?.length) return;
    // get min end time
    let smallestEndTime = parseInt(validDeals?.[0]?.endTime);
    validDeals?.forEach((deal) => {
      smallestEndTime =
        parseInt(deal.endTime) < smallestEndTime
          ? parseInt(deal.endTime)
          : smallestEndTime;
    });
    // remain time
    const remainTime =
      new Date(smallestEndTime * 1000).getTime() - new Date().getTime();

    //ignore invalid remainTime
    if (!remainTime || remainTime <= 0) {
      return;
    }

    // set timeout to revalidate deals
    const revalidateDeals = setTimeout(() => {
      const remainDeals = validDeals?.filter((deal) =>
        checkExpiryDateTimestamp(deal.startTime, deal.endTime),
      );
      if (!remainDeals?.find((deal) => deal.id === activeTabId)) {
        setActiveTabId(remainDeals?.[0]?.id);
      }
      setValidDeals(remainDeals);
    }, remainTime);

    return () => clearTimeout(revalidateDeals);
  }, [activeTabId, JSON.stringify(validDeals)]);

  const transformTabHeader = (
    item: MultiTabSkuBlockCookedItem,
  ): TabHeaderProps => {
    return {
      id: parseInt(item.id),
      title: item.label,
      description: item.subLabel,
      endAt: getTime(item.endTime),
      backgroundImage: item.imageUrl,
      link:
        item.url ||
        `${convertObjectDiscoveryToQueryStringSearch(item.paramsObject)}`,
      isDisplayCountdown: item.isDisplayCountdown ?? true,
    };
  };

  const productsCarousel = useMemo(() => {
    const defaultValue: ProductListConfig = { products: [] };
    // get product carousel from server
    const getServerProductCarousel = () => {
      if (!ssrProducts?.length) return defaultValue;
      return {
        products: ssrProducts,
        hideCtaButton: true,
      };
    };

    // check if there are products from client
    // yes --> set products = products from client
    // no --> check if activeTab === first tab
    // yes --> set products = product from server
    // no --> set products = null
    const productsCarousel = result?.products?.length
      ? {
          products: result.products,
          hideCtaButton: false,
        }
      : activeTabId === validDeals?.[0]?.id
      ? getServerProductCarousel()
      : defaultValue;
    return productsCarousel;
  }, [activeTabId, result?.products, ssrProducts]);

  // if no item --> return
  if (validDeals?.length === 0 || !activeTabId) {
    return null;
  }

  return (
    <HotDealTab
      tabs={validDeals?.map(transformTabHeader) || []}
      activeTabId={parseInt(activeTabId)}
      productsCarousel={productsCarousel}
      handleChangeTab={(id) => setActiveTabId(id.toString())}
    />
  );
};
